import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  input,
  output,
  signal
} from '@angular/core';
import {
  INotificationMatrixRow,
  NOTIFICATION_MATRIX_COLUMNS,
  NOTIFICATION_MATRIX_ROWS,
  NotificationMatrixColumn,
  isLegacyHiringLeads
} from '@configuration/shared/components/notification-matrix/notification-matrix.rows';
import { ICustomNotification } from '@shared/interfaces/custom-notification.interface';
import { ISetting } from '@shared/interfaces/setting.interface';
import { CustomNotificationsService } from '@shared/services/custom-notifications.service';
import { LoggerService } from '@shared/services/logger.service';
import { ToastService } from '@shared/services/toast.service';
import { TranslateService } from '@ngx-translate/core';

export interface INotificationMatrixItem {
  def: INotificationMatrixRow;
  row: ICustomNotification;
}

/**
 * "Team notifications" matrix: one row per event, one checkbox per recipient
 * column. Every cell saves on its own as soon as it is toggled (optimistic,
 * rolled back if the API refuses).
 */
@Component({
  selector: 'app-notification-matrix',
  templateUrl: './notification-matrix.component.html',
  styleUrls: ['./notification-matrix.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class NotificationMatrixComponent implements OnInit {
  public readonly settings = input.required<ISetting>();
  /** A legacy hiring-leads firm asked to edit its per-address list. */
  public readonly manageLeadList = output<ICustomNotification>();

  public readonly columns: NotificationMatrixColumn[] = NOTIFICATION_MATRIX_COLUMNS;
  public readonly rows = signal<ICustomNotification[]>([]);
  public readonly isLoading = signal<boolean>(true);
  public readonly hasLoadError = signal<boolean>(false);
  public readonly saving = signal<ReadonlySet<string>>(new Set<string>());

  public readonly visibleRows = computed<INotificationMatrixItem[]>(() => {
    const settings = this.settings();
    const rows = this.rows();

    return NOTIFICATION_MATRIX_ROWS.filter((def: INotificationMatrixRow) =>
      def.isVisible(settings)
    )
      .map((def: INotificationMatrixRow) => ({
        def,
        row: rows.find(
          (row: ICustomNotification) => row.notification_type === def.type
        )
      }))
      .filter((item): item is INotificationMatrixItem => !!item.row);
  });

  private readonly constructorName: string = String(this.constructor.name);

  constructor(
    private readonly _customNotifications: CustomNotificationsService,
    private readonly _logger: LoggerService,
    private readonly _toast: ToastService,
    private readonly _translate: TranslateService
  ) {}

  ngOnInit(): void {
    const url = 'GET /custom_notifications';

    this._customNotifications.list().subscribe({
      next: (rows: ICustomNotification[]) => {
        this._logger.info(this.constructorName, url, rows);
        this.rows.set(rows);
        this.isLoading.set(false);
      },
      error: (err: any) => {
        this._logger.error(this.constructorName, url, err);
        this.hasLoadError.set(true);
        this.isLoading.set(false);
      }
    });
  }

  public isLegacyLeads(row: ICustomNotification): boolean {
    return isLegacyHiringLeads(row.notification_type, this.settings());
  }

  public isSaving(row: ICustomNotification, column: string): boolean {
    return this.saving().has(this.cellKey(row, column));
  }

  public cellId(row: ICustomNotification, column: string): string {
    return `${row.notification_type}_${column}`;
  }

  public toggle(row: ICustomNotification, column: NotificationMatrixColumn): void {
    const value = !row[column];

    this.persist(row, column, { [column]: value });
  }

  public onEmailsChange(row: ICustomNotification, emails: string[]): void {
    this.persist(row, 'custom_emails', { custom_emails: emails });
  }

  /** Re-applies a row returned by the API (e.g. after the legacy leads modal saved). */
  public replaceRow(row: ICustomNotification): void {
    this.rows.update((rows: ICustomNotification[]) =>
      rows.map((current: ICustomNotification) =>
        current.id === row.id ? row : current
      )
    );
  }

  private persist(
    row: ICustomNotification,
    column: string,
    attributes: Partial<ICustomNotification>
  ): void {
    const key = this.cellKey(row, column);
    const previous: ICustomNotification = row;
    const url = `PATCH /custom_notifications/${row.id}`;

    this.replaceRow({ ...row, ...attributes });
    this.saving.update((set: ReadonlySet<string>) => new Set(set).add(key));

    this._customNotifications.patch(row.id, attributes).subscribe({
      next: (saved: ICustomNotification) => {
        this._logger.info(this.constructorName, url, saved);
        this.replaceRow(saved);
        this.done(key);
        this._toast.success(
          this._translate.instant('SETTINGS.CONFIGURATION.TEAM_NOTIFICATIONS.SAVED')
        );
      },
      error: (err: any) => {
        this._logger.error(this.constructorName, url, err);
        this.replaceRow(previous);
        this.done(key);
        this._toast.error(
          err?.[0]?.detail ||
            this._translate.instant('SETTINGS.CONFIGURATION.TEAM_NOTIFICATIONS.SAVE_FAILED')
        );
      }
    });
  }

  private done(key: string): void {
    this.saving.update((set: ReadonlySet<string>) => {
      const next = new Set(set);
      next.delete(key);

      return next;
    });
  }

  private cellKey(row: ICustomNotification, column: string): string {
    return `${row.notification_type}:${column}`;
  }
}
